import { create } from "zustand";
import { functions as firebaseFunctions } from "../firebase/config";

// helpers
export const keyOf = (item) =>
  `${item.type}:${item.id}${item.variant ? `:${item.variant}` : ""}`;

const emptyPromoState = () => ({
  code: "",
  applied: false,
  type: null,
  value: 0,
  message: "",
  promoId: null,
  validatedAt: null,
});

const normalizePromoState = (promo) => ({
  code: promo?.code || "",
  applied: !!promo?.applied,
  type: promo?.type ?? null,
  value: Number(promo?.value || 0),
  message: promo?.message || "",
  promoId: promo?.promoId || null,
  validatedAt: promo?.validatedAt || null,
});

const emptyGiftState = () => ({
  enabled: false,
  recipient: null,
});

const normalizeGiftState = (gift) => ({
  enabled: !!gift?.enabled,
  recipient: gift?.recipient?.id
    ? {
        id: String(gift.recipient.id),
        username: gift.recipient.username || "",
        name: gift.recipient.name || "",
        avatar: gift.recipient.avatar || "",
      }
    : null,
});

const getCallableErrorMessage = (err, fallback) => {
  const raw = String(
    err?.message || err?.details || err?.response?.data?.error || fallback
  );
  const cleaned = raw.replace(/^([a-z-]+):\s*/i, "").trim();
  return cleaned || fallback;
};

export const computeTotals = (items = [], promo = null) => {
  let totalUSD = 0;
  let totalTokens = 0;

  for (const it of items) {
    const qty = Number(it.qty || 1);
    if (it.priceUSD) totalUSD += Number(it.priceUSD) * qty; // cents
    if (it.priceTokens) totalTokens += Number(it.priceTokens) * qty;
  }

  // promo only applies to USD subtotal
  const subtotalUSD = totalUSD;

  let discountUSD = 0; // cents
  if (promo && promo.applied && subtotalUSD > 0) {
    if (promo.type === "percent") {
      discountUSD = Math.round(subtotalUSD * (Number(promo.value || 0) / 100));
    } else if (promo.type === "fixed") {
      discountUSD = Number(promo.value || 0);
    }

    // clamp discount to subtotal
    discountUSD = Math.max(0, Math.min(discountUSD, subtotalUSD));
  }

  const totalAfterDiscountUSD = Math.max(0, subtotalUSD - discountUSD);

  return {
    totalUSD: subtotalUSD,
    totalTokens,
    discountUSD,
    totalAfterDiscountUSD,
  };
};

export const useCartStore = create((set, get) => ({
  cartId: null,
  items: [], // { key, type, id, name, image, priceUSD?, priceTokens?, qty, meta }
  open: false,
  locked: false,
  purchased: false,
  gift: emptyGiftState(),

  // Promo
  promo: {
    ...emptyPromoState(),
  },

  setPromoCode: (code) =>
    set((state) => ({
      promo: { ...state.promo, code },
    })),

  clearPromo: async () => {
    const { cartId } = get();
    const fallbackPromo = emptyPromoState();

    set({ promo: fallbackPromo });

    if (!cartId) return { ok: true, promo: fallbackPromo };

    try {
      const clearPromoCallable = firebaseFunctions.httpsCallable("clearCartPromo");
      const result = await clearPromoCallable({ cartId });
      const nextPromo = normalizePromoState(result?.data?.promo);
      set({ promo: nextPromo });
      return { ok: true, promo: nextPromo, totals: result?.data?.totals };
    } catch (err) {
      const message = getCallableErrorMessage(
        err,
        "Couldn't remove promo code right now."
      );
      set({
        promo: {
          ...fallbackPromo,
          message,
        },
      });
      return { ok: false, promo: fallbackPromo, error: message };
    }
  },

  applyPromo: async () => {
    const { promo, items, cartId } = get();

    const raw = (promo.code || "").trim();
    const code = raw.toUpperCase();

    if (!code) {
      set((state) => ({
        promo: {
          ...state.promo,
          applied: false,
          type: null,
          value: 0,
          promoId: null,
          validatedAt: null,
          message: "Enter a code.",
        },
      }));
      return { ok: false };
    }

    // Optional: block promo if there are no USD items
    const { totalUSD } = computeTotals(items, null);
    if (!totalUSD) {
      set((state) => ({
        promo: {
          ...state.promo,
          applied: false,
          type: null,
          value: 0,
          promoId: null,
          validatedAt: null,
          message: "Promo codes apply to USD items only.",
        },
      }));
      return { ok: false };
    }

    if (!cartId) {
      set((state) => ({
        promo: {
          ...state.promo,
          applied: false,
          type: null,
          value: 0,
          promoId: null,
          validatedAt: null,
          message: "Please wait a moment and try again.",
        },
      }));
      return { ok: false };
    }

    try {
      const validatePromoCallable =
        firebaseFunctions.httpsCallable("validateCartPromo");
      const result = await validatePromoCallable({ cartId, code });
      const nextPromo = normalizePromoState(result?.data?.promo);
      set({ promo: nextPromo });
      return {
        ok: !!result?.data?.ok,
        promo: nextPromo,
        totals: result?.data?.totals,
      };
    } catch (err) {
      const message = getCallableErrorMessage(
        err,
        "Couldn't validate promo code right now."
      );
      set((state) => ({
        promo: {
          ...state.promo,
          code,
          applied: false,
          type: null,
          value: 0,
          promoId: null,
          validatedAt: null,
          message,
        },
      }));
      return { ok: false, error: message };
    }
  },

  // UI
  openDrawer: () => set({ open: true }),
  closeDrawer: () => set({ open: false }),
  setGiftEnabled: (enabled) =>
    set((state) => ({
      gift: {
        ...state.gift,
        enabled: !!enabled,
        recipient: enabled ? state.gift.recipient : null,
      },
    })),
  setGiftRecipient: (recipient) =>
    set((state) => ({
      gift: {
        ...state.gift,
        recipient: recipient?.id
          ? {
              id: String(recipient.id),
              username: recipient.username || "",
              name: recipient.name || "",
              avatar: recipient.avatar || "",
            }
          : null,
      },
    })),
  clearGift: () => set({ gift: emptyGiftState() }),

  // Mutations
  addItem: (partial) => {
    console.warn("this is the partial", partial);
    set((state) => {
      const incoming = { qty: 1, ...partial };
      const key = incoming.key || keyOf(incoming);

      const next = [...state.items];
      const idx = next.findIndex((x) => x.key === key);

      const isPack = incoming.type === "pack";

      // Helper: normalize pack metas
      const getIncomingMetas = () => {
        // Preferred: caller passes metas (array)
        if (Array.isArray(incoming.metas) && incoming.metas.length)
          return incoming.metas;

        // Back-compat: caller passes meta (single object)
        if (incoming.meta && typeof incoming.meta === "object")
          return [incoming.meta];

        // If you generate pack contents elsewhere, you can leave this empty
        // and push generated meta objects from outside.
        return [];
      };

      if (idx >= 0) {
        if (isPack) {
          const existing = next[idx];

          const existingMetas =
            Array.isArray(existing.metas) && existing.metas.length
              ? existing.metas
              : existing.meta && typeof existing.meta === "object"
              ? [existing.meta]
              : [];

          const incomingMetas = getIncomingMetas();

          const { meta: _oldMeta, ...existingNoMeta } = existing;

          next[idx] = {
            ...existingNoMeta,
            qty: (existing.qty || 1) + (incoming.qty || 1),
            metas: [...existingMetas, ...incomingMetas],
          };
        } else {
          next[idx] = {
            ...next[idx],
            qty: (next[idx].qty || 1) + (incoming.qty || 1),
          };
        }
      } else {
        if (isPack) {
          const incomingMetas = getIncomingMetas();

          // If qty > 1 is ever passed for packs, you likely want one meta per qty.
          // This duplicates the *provided* meta(s) to match qty; if you want them
          // random, generate distinct metas before calling addItem.
          const qty = incoming.qty || 1;
          const metas =
            qty > 1 && incomingMetas.length
              ? Array.from({ length: qty }, () => incomingMetas[0])
              : incomingMetas;

          const { meta, ...rest } = incoming; // drop legacy meta
          next.push({ ...rest, key, qty, metas });
        } else {
          next.push({ ...incoming, key });
        }
      }

      return { items: next };
    });
  },

  setQty: (key, qty) =>
    set((state) => ({
      items: state.items.map((x) =>
        x.key === key ? { ...x, qty: Math.max(1, qty) } : x
      ),
    })),
  inc: (key) =>
    set((state) => ({
      items: state.items.map((x) =>
        x.key === key ? { ...x, qty: (x.qty || 1) + 1 } : x
      ),
    })),
  dec: (key, type = null) =>
    set((state) => ({
      items: state.items.map((x) => {
        if (x.key !== key) return x;

        const nextQty = Math.max(1, (x.qty || 1) - 1);

        // Non-pack items: just decrement qty
        if (type !== "pack") {
          return {
            ...x,
            qty: nextQty,
          };
        }

        // Pack items: decrement qty AND remove last meta
        const metas = Array.isArray(x.metas) ? x.metas : [];

        return {
          ...x,
          qty: nextQty,
          metas: metas.slice(0, Math.max(0, metas.length - 1)),
        };
      }),
    })),

  remove: (key) =>
    set((state) => ({ items: state.items.filter((x) => x.key !== key) })),
  clear: () =>
    set({
      items: [],
      purchased: false,
      locked: false,
      gift: emptyGiftState(),
      promo: {
        ...emptyPromoState(),
      },
    }),

  // Flags + identity
  setCartId: (cartId) => set({ cartId }),
  setLocked: (locked) => set({ locked }),
  setPurchased: (purchased) => set({ purchased }),

  // Hydrate from Firestore
  hydrateFromDoc: (doc) =>
    set({
      cartId: doc.id,
      items: doc.items || [],
      locked: !!doc.locked,
      purchased: !!doc.purchased,
      gift: doc.gift ? normalizeGiftState(doc.gift) : emptyGiftState(),
      promo: doc.promo
        ? normalizePromoState(doc.promo)
        : emptyPromoState(),
    }),
}));
